import {
  FORCED_DARK_ROUTE_PREFIXES,
  matchesRoutePrefix,
} from "@/lib/constants";
import {
  DARK_MODE_VARIANT_STORAGE_KEY,
  DARK_MODE_VARIANTS,
  DEFAULT_DARK_VARIANT_SELECTION,
} from "@/lib/dark-mode-variants";
import {
  LIGHT_MODE_VARIANT_STORAGE_KEY,
  LIGHT_MODE_VARIANTS,
  DEFAULT_LIGHT_VARIANT_SELECTION,
} from "@/lib/light-mode-variants";

export const PREVIEW_MODE_STORAGE_KEY = "zom-preview-mode";
export const PREVIEW_COLORS_STORAGE_KEY = "zom-preview-theme-colors";
export const PREVIEW_CSS_STORAGE_KEY = "zom-preview-theme-colors-css";
export const THEME_OVERRIDE_STYLE_ID = "zom-theme-overrides";

const appearancePolicy = {
  previewRoot: "/zom-ui-kit",
  frameAttribute: "data-zom-theme-preview",
  forcedDarkRoutes: FORCED_DARK_ROUTE_PREFIXES,
  productModeKey: "theme",
  previewModeKey: PREVIEW_MODE_STORAGE_KEY,
  darkKey: DARK_MODE_VARIANT_STORAGE_KEY,
  lightKey: LIGHT_MODE_VARIANT_STORAGE_KEY,
  cssKey: PREVIEW_CSS_STORAGE_KEY,
  styleId: THEME_OVERRIDE_STYLE_ID,
  defaultDark: DEFAULT_DARK_VARIANT_SELECTION,
  defaultLight: DEFAULT_LIGHT_VARIANT_SELECTION,
  darkVariants: [...DARK_MODE_VARIANTS.map(({ id }) => id), "none"],
  lightVariants: [...LIGHT_MODE_VARIANTS.map(({ id }) => id), "none"],
  migrations: [
    [PREVIEW_MODE_STORAGE_KEY, "theme"],
    [DARK_MODE_VARIANT_STORAGE_KEY, "zom-dark-variant"],
    [LIGHT_MODE_VARIANT_STORAGE_KEY, "zom-light-variant"],
    [PREVIEW_COLORS_STORAGE_KEY, "zom-theme-colors"],
    [PREVIEW_CSS_STORAGE_KEY, "zom-theme-colors-css"],
  ],
};

type AppearancePolicy = typeof appearancePolicy;

/**
 * Kept closure-free so bootstrap and React execute the same scope policy.
 * The window arrives as an argument: the server compile folds
 * `typeof window` to "undefined", which would strip the frame check from the
 * serialized bootstrap.
 */
function resolvePreviewScope(
  policy: AppearancePolicy,
  pathname: string,
  win: Window | undefined,
): boolean {
  const inKit = (path: string) =>
    path === policy.previewRoot || path.startsWith(policy.previewRoot + "/");
  if (inKit(pathname)) return true;
  if (!win) return false;
  try {
    return (
      win.parent !== win &&
      win.frameElement?.getAttribute(policy.frameAttribute) === "true" &&
      inKit(win.parent.location.pathname)
    );
  } catch {
    // Cross-origin embedding never opts a document into design customization.
    return false;
  }
}

export function isThemePreviewDocument(pathname?: string): boolean {
  const win = typeof window === "undefined" ? undefined : window;
  return resolvePreviewScope(
    appearancePolicy,
    pathname ?? win?.location.pathname ?? "",
    win,
  );
}

export function readAppearanceMode(
  preview: boolean,
): "light" | "dark" | "system" {
  try {
    const value = window.localStorage.getItem(
      preview
        ? appearancePolicy.previewModeKey
        : appearancePolicy.productModeKey,
    );
    return value === "light" || value === "dark" ? value : "system";
  } catch {
    return "system";
  }
}

/**
 * Its source also runs before hydration. Call only arguments and browser
 * globals here: no imported calls and no `typeof` global checks.
 */
function applyDocumentAppearance(
  policy: AppearancePolicy,
  resolvePreview: typeof resolvePreviewScope,
  matchesRoute: typeof matchesRoutePrefix,
  applyMode: boolean,
): void {
  const root = document.documentElement;
  const pathname = window.location.pathname;
  const preview = resolvePreview(policy, pathname, window);
  const read = (key: string): string | null => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  if (preview) {
    // Copy legacy design settings only in previews. Never overwrite or remove
    // the old keys, and never migrate them into a normal product document.
    for (const [next, previous] of policy.migrations) {
      if (!next || !previous || read(next) !== null) continue;
      const saved = read(previous);
      if (saved === null) continue;
      try {
        window.localStorage.setItem(next, saved);
      } catch {
        // Defaults still apply when persistence is unavailable.
      }
    }
  }

  const dark = preview ? read(policy.darkKey) : null;
  const light = preview ? read(policy.lightKey) : null;
  const darkVariant =
    dark && policy.darkVariants.includes(dark) ? dark : policy.defaultDark;
  const lightVariant =
    light && policy.lightVariants.includes(light) ? light : policy.defaultLight;
  if (darkVariant === "none") root.removeAttribute("data-dark-variant");
  else root.setAttribute("data-dark-variant", darkVariant);
  if (lightVariant === "none") root.removeAttribute("data-light-variant");
  else root.setAttribute("data-light-variant", lightVariant);

  const css = preview ? read(policy.cssKey) : null;
  const existing = document.getElementById(policy.styleId);
  if (css) {
    const style =
      existing instanceof HTMLStyleElement
        ? existing
        : document.createElement("style");
    style.id = policy.styleId;
    style.textContent = css;
    document.head.appendChild(style);
  } else {
    existing?.remove();
  }

  if (!applyMode) return;
  const modeKey = preview ? policy.previewModeKey : policy.productModeKey;
  const storedMode = read(modeKey);
  const mode =
    storedMode === "light" || storedMode === "dark" ? storedMode : "system";
  if (storedMode !== null && storedMode !== mode) {
    try {
      window.localStorage.setItem(modeKey, mode);
    } catch {
      // Initial appearance still has a deterministic fallback.
    }
  }
  // Preview frames stay forced dark too, so they match production.
  const forceDark = matchesRoute(pathname, policy.forcedDarkRoutes);
  const darkMode =
    forceDark ||
    mode === "dark" ||
    (mode === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);
  root.classList.toggle("dark", darkMode);
  root.classList.toggle("light", !darkMode);
}

/** React owns mode through next-themes; this owns only variant/token scope. */
export function applyRouteAppearance(): void {
  applyDocumentAppearance(
    appearancePolicy,
    resolvePreviewScope,
    matchesRoutePrefix,
    false,
  );
}

export const DOCUMENT_APPEARANCE_BOOTSTRAP = `(${applyDocumentAppearance.toString()})(${JSON.stringify(appearancePolicy)},${resolvePreviewScope.toString()},${matchesRoutePrefix.toString()},true);`;
